import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth";
import { confirmDomainVerification, requestDomainVerification } from "../../api/subscriptions";
import { recordFirstAdminLogin } from "../../api/users";

const PROJECT_ID = process.env.REACT_APP_SIGNUP_PROJECT_ID;

export default function VerifyEmail() {
  const navigate = useNavigate();
  const { user, subscription, loading, subscriptionLoading, refreshSubscription } = useAuth();
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [blocked, setBlocked] = useState(false);

  if (loading || subscriptionLoading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (subscription?.account?.id && subscription.account.verificationStatus !== "pending") {
    return <Navigate to="/posts/attraction" replace />;
  }

  function showError(e) {
    setBlocked(e?.data?.error?.code === "CANNOT_PROCESS");
    setError(e?.message || "Something went wrong. Please try again.");
  }

  async function sendCode() {
    setBusy(true);
    setError(null);
    setBlocked(false);
    setMessage(null);
    try {
      await requestDomainVerification({ projectId: PROJECT_ID });
      setSent(true);
      setMessage(`We sent a 6-digit code to ${user.email}. It expires in 15 minutes.`);
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  }

  async function verify(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setBlocked(false);
    try {
      await confirmDomainVerification({ projectId: PROJECT_ID, code: code.trim() });
      await refreshSubscription();
      try {
        await recordFirstAdminLogin();
      } catch (eventError) {
        console.warn("Unable to record admin-first-login event", eventError);
      }
      navigate("/posts/profile", { replace: true });
    } catch (err) {
      showError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-sm-10 col-md-7 col-lg-5">
          <div className="text-center mb-4">
            <h2 className="fw-bold mb-1">Verify your email</h2>
            <div className="text-muted">
              Confirm you own <strong>{user.email}</strong> to activate your account and claim your listing.
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-body p-4">
              {!sent ? (
                <button type="button" className="btn btn-primary w-100" onClick={sendCode} disabled={busy}>
                  {busy ? "Sending…" : "Send me a code"}
                </button>
              ) : (
                <form onSubmit={verify}>
                  <div className="mb-3 text-start">
                    <label className="form-label">Verification code</label>
                    <input
                      className="form-control"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-primary w-100" disabled={busy}>
                    {busy ? "Verifying…" : "Verify"}
                  </button>
                  <button type="button" className="btn btn-link w-100 mt-2" onClick={sendCode} disabled={busy}>
                    Send a new code
                  </button>
                </form>
              )}

              {message ? <div className="alert alert-success mt-3 mb-0 py-2">{message}</div> : null}
              {error ? (
                <div role="alert" className="alert alert-danger mt-3 mb-0 py-2">
                  {error}
                  {blocked ? <div className="mt-2"><a href="mailto:help@bouncehouse.com">help@bouncehouse.com</a></div> : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
